import React from 'react'
import { Container } from '../Container'
import { Chip, Typography, useTheme } from '@material-ui/core'
import { HelpVideo } from '../../navigation/layouts/Lori/types'
import VisibilityIcon from '@material-ui/icons/Visibility'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn'
import HelpIcon from '@material-ui/icons/Help'
import BuildIcon from '@material-ui/icons/Build'
import SchoolIcon from '@material-ui/icons/School'
import { ReactComponent as HomeSBIcon } from '../../assets/icons/grid.svg'
import { ReactComponent as CalendarSBIcon } from '../../assets/icons/group-39654.svg'
import { ReactComponent as HelpCircleSBIcon } from '../../assets/icons/help-circle.svg'
import { ReactComponent as ListSBIcon } from '../../assets/icons/action-item.svg'
import { ReactComponent as SettingsSBIcon } from '../../assets/icons/settings.svg'
import { ReactComponent as MessageSBIcon } from '../../assets/icons/vector-1.svg'
import { ReactComponent as UsersSBIcon } from '../../assets/icons/ion_people-outline.svg'
import { ReactComponent as StarSBIcon } from '../../assets/icons/star.svg'
import { ReactComponent as DollarSignSBIcon } from '../../assets/icons/dollar-sign.svg'
import { ReactComponent as ToolSBIcon } from '../../assets/icons/tool.svg'
import { ReactComponent as FileSBIcon } from '../../assets/icons/file.svg'
import { ReactComponent as ClipboardTickSBIcon } from '../../assets/icons/clipboard-tick.svg'
import { FileCopy, TrendingUp, Assignment } from '@material-ui/icons'

interface TrainingVideoCardProps {
    video: HelpVideo
    onVideoClick?: (video: HelpVideo) => void
}

export const TrainingVideoCard: React.FC<TrainingVideoCardProps> = ({
    video,
    onVideoClick = () => window.open(video.video_url, '_blank'),
}) => {
    const theme = useTheme()

    const handleVideoClick = () => {
        onVideoClick(video)
    }

    // Format the date to be more readable
    const formatDate = (dateString: string) => {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })
    }

    // Get the appropriate icon based on category
    const getCategoryIcon = () => {
        switch (video.category) {
            case 'Inspections':
                return (
                    <ClipboardTickSBIcon
                        width="24"
                        height="24"
                        style={{
                            filter: 'brightness(0) invert(1)', // Force white color
                        }}
                    />
                )
            case 'Workforce':
                return (
                    <UsersSBIcon
                        width="24"
                        height="24"
                        style={{
                            filter: 'brightness(0) invert(1)',
                        }}
                    />
                )
            case 'Calendar':
                return (
                    <CalendarSBIcon
                        width="24"
                        height="24"
                        style={{
                            filter: 'brightness(0) invert(1)',
                        }}
                    />
                )
            case 'Reports':
                return (
                    <FileCopy
                        width="24"
                        height="24"
                        style={{
                            filter: 'brightness(0) invert(1)',
                        }}
                    />
                )
            case 'Projects':
                return (
                    <AssignmentTurnedInIcon
                        width="24"
                        height="24"
                        style={{ filter: 'brightness(0) invert(1)' }}
                    />
                )
            case 'Analytics':
                return (
                    <TrendingUp
                        width="24"
                        height="24"
                        style={{ filter: 'brightness(0) invert(1)' }}
                    />
                )
            case 'Workorders':
                return (
                    <Assignment
                        width="24"
                        height="24"
                        style={{ filter: 'brightness(0) invert(1)' }}
                    />
                )
            default:
                return null
        }
    }

    // Check if we have a predefined icon for this category
    const categoryIcon = getCategoryIcon()

    return (
        <Container
            style={{
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: theme.shape.borderRadius,
                overflow: 'hidden',
                backgroundColor: '#fafafa',
                marginBottom: theme.spacing(2),
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                transition: 'transform 0.2s, box-shadow 0.2s',
            }}
        >
            {/* Video container - clickable */}
            <Container
                style={{
                    display: 'flex',
                    padding: theme.spacing(2),
                    cursor: 'pointer',
                    alignItems: 'center', // Center items vertically
                }}
                onClick={handleVideoClick}
                onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#f0f0f0'
                    e.currentTarget.style.transform = 'translateY(-2px)'
                    e.currentTarget.style.boxShadow =
                        '0 4px 8px rgba(0,0,0,0.1)'
                }}
                onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#fafafa'
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = 'none'
                }}
            >
                {/* Thumbnail */}
                <div
                    style={{
                        width: 200,
                        minWidth: 200,
                        height: 112, // 16:9 aspect ratio
                        marginRight: theme.spacing(2),
                        position: 'relative',
                        alignSelf: 'center', // Ensure thumbnail is centered
                    }}
                >
                    <img
                        src={video.thumbnail}
                        alt={video.title}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: theme.shape.borderRadius,
                        }}
                    />
                    {/* Duration overlay */}
                    <Typography
                        variant="caption"
                        style={{
                            position: 'absolute',
                            bottom: 4,
                            right: 4,
                            backgroundColor: 'rgba(0, 0, 0, 0.75)',
                            color: 'white',
                            padding: '2px 4px',
                            borderRadius: 2,
                        }}
                    >
                        {typeof video.duration === 'number'
                            ? `${Math.floor(video.duration / 60)}:${(
                                  video.duration % 60
                              )
                                  .toString()
                                  .padStart(2, '0')}`
                            : video.duration}
                    </Typography>
                </div>

                {/* Content */}
                <div
                    style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {/* Title row with category chip or icon */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: theme.spacing(1),
                        }}
                    >
                        <Typography
                            variant="h6"
                            style={{
                                fontWeight: 600,
                            }}
                        >
                            {video.title}
                        </Typography>

                        {categoryIcon ? (
                            <Container
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#1E2030', // Darker background to match sidebar
                                    borderRadius: '50%',
                                    width: 42,
                                    height: 42,
                                    marginLeft: theme.spacing(1),
                                }}
                            >
                                {categoryIcon}
                            </Container>
                        ) : (
                            <Chip
                                label={video.category}
                                size="medium"
                                style={{
                                    marginLeft: theme.spacing(1),
                                    backgroundColor: '#1E2030', // Match the icon container background
                                    color: '#FFFFFF',
                                    fontWeight: 700,
                                    height: 28, // Slightly taller for better legibility
                                }}
                            />
                        )}
                    </div>

                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{
                            marginBottom: theme.spacing(1),
                        }}
                    >
                        {video.summary}
                    </Typography>
                </div>
            </Container>

            {/* Topics and metadata section */}
            <Container
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    padding: theme.spacing(1, 2, 2, 2),
                    borderTop: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    justifyContent: 'space-between',
                }}
            >
                {/* Topics */}
                <div style={{ display: 'flex', flexWrap: 'wrap', flex: 1 }}>
                    {Array.isArray(video.key_topics) &&
                        video.key_topics.map((topic, index) => (
                            <Chip
                                key={index}
                                label={topic}
                                size="small"
                                style={{
                                    margin: theme.spacing(0.5),
                                    backgroundColor: theme.palette.grey[100],
                                    color: theme.palette.text.primary,
                                }}
                            />
                        ))}
                </div>

                {/* Meta information */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: theme.spacing(1),
                        color: theme.palette.text.secondary,
                        flexShrink: 0,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginRight: theme.spacing(2),
                        }}
                    >
                        <VisibilityIcon
                            fontSize="small"
                            style={{ marginRight: 4 }}
                        />
                        <Typography variant="body2">
                            {video.views} views
                        </Typography>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarTodayIcon
                            fontSize="small"
                            style={{ marginRight: 4 }}
                        />
                        <Typography variant="body2">
                            {formatDate(video.added_at)}
                        </Typography>
                    </div>
                </div>
            </Container>
        </Container>
    )
}
